import type { Checkpoint } from "@/types/api";

/** What the Checkpoints tab shows: the current position of each source
 *  partition, and how many earlier ones the engine also keeps. */
export interface CheckpointView {
  current: Checkpoint[];
  earlier: number;
}

/** The partition a checkpoint belongs to. Kafka keeps one per topic and
 *  partition; every other source has one position, and the rest are its
 *  history. */
function partitionOf(cp: Checkpoint): string {
  return cp.cursor.source === "kafka"
    ? `kafka:${cp.cursor.topic}:${cp.cursor.partition}`
    : "position";
}

/** The current checkpoint of each partition, in the order the engine lists
 *  them. The engine lists a partition's checkpoints current first: by
 *  position for change capture, where a save that lands late can carry a
 *  newer time than the position it replaced, so the first one listed wins,
 *  never the latest timestamp. */
export function currentCheckpoints(checkpoints: Checkpoint[]): CheckpointView {
  const current = new Map<string, Checkpoint>();
  for (const cp of checkpoints) {
    const key = partitionOf(cp);
    if (!current.has(key)) current.set(key, cp);
  }
  return {
    current: [...current.values()],
    earlier: checkpoints.length - current.size,
  };
}

/** How long ago `updatedAt` was, in words; `undefined` when it does not
 *  read as a time. */
export function checkpointAge(
  updatedAt: string,
  now: number,
): string | undefined {
  const at = Date.parse(updatedAt.replace(" UTC", "Z").replace(" ", "T"));
  if (Number.isNaN(at)) return undefined;
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
